import { 
  Sparkles, Gift, MapPin, Calendar, TrendingUp, 
  Star, Clock, ChevronRight, Home, Search, Wallet,
  User, QrCode, Store, Coffee, Pizza, ShoppingBag,
  Percent, Tag, Trophy, Bell, Menu
} from 'lucide-react';
import { components, gradients } from '../theme/wunderCityTheme';

function Dashboard() {

  const stats = {
    totalPoints: 2847,
    dollarValue: 28.47,
    thisMonth: 450,
    expiringSoon: 3
  };

  const featuredBusinesses = [
    {
      id: 1,
      name: "Coastal Coffee Co.",
      category: "Coffee & Cafe",
      distance: "0.3 mi",
      offer: "2x points today",
      image: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400",
      icon: Coffee,
      rating: 4.8,
      pointsMultiplier: 2
    },
    {
      id: 2,  
      name: "Marina Pizza",
      category: "Restaurant",
      distance: "0.5 mi",
      offer: "Free drink with pizza",
      image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400",
      icon: Pizza,
      rating: 4.6,
      hasPromo: true
    },
    {
      id: 3,
      name: "Bayfront Boutique", 
      category: "Shopping",
      distance: "0.2 mi",
      offer: "15% off with 500 pts",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400",
      icon: ShoppingBag,
      rating: 4.9,
      pointsDiscount: 15
    }
  ];

  const upcomingEvents = [
    {
      id: 1,
      name: "Downtown Art Walk",
      date: "Sat, Nov 18",
      time: "5:00 PM - 9:00 PM",
      location: "Palafox Street",
      attendees: 234,
      bonusPoints: 100
    },
    {
      id: 2,
      name: "Farmers Market",
      date: "Sun, Nov 19", 
      time: "8:00 AM - 2:00 PM",
      location: "Martin Luther King Plaza",
      attendees: 512,
      bonusPoints: 50
    }
  ];

  const recentActivity = [
    {
      id: 1,
      business: "Coastal Coffee Co.",
      points: 45,
      time: "2 hours ago",
      type: "earned"
    },
    {
      id: 2,
      business: "City Bookstore",
      points: 200,
      time: "Yesterday",
      type: "redeemed"
    },
    {
      id: 3,
      business: "Marina Pizza", 
      points: 67,
      time: "3 days ago",
      type: "earned"
    }
  ];

  return (
    <div className="w-full">
      {/* Section Header */}
      <div className="text-center px-4 md:px-8 pb-8 md:pb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          3 Approaches to a Home Dashboard
        </h2>
        <p className="text-lg md:text-xl text-gray-600">
          Different ways to prioritize and communicate key pieces of information quickly
        </p>
      </div>

      {/* Dashboard Screens */}
      <div className="flex flex-col lg:flex-row gap-8 p-4 md:p-8 items-center lg:justify-center overflow-x-auto">
        
        {/* Solution 1: Points-Focused Hero */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-11 h-full bg-gray-50 flex flex-col">
                  {/* Header */}
                  <div className="px-6 pt-4 pb-2 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Good morning,</p>
                        <h1 className="text-2xl font-bold text-gray-900">Sarah!</h1>
                      </div>
                      <button className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <Bell className="w-5 h-5 text-gray-700" />
                      </button>
                    </div>
                  </div>

                  {/* Points Card */}
                  <div className="px-6 -mt-2">
                    <div className={`${gradients.skyBlue} rounded-3xl p-6 shadow-lg`}>
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <p className="text-sky-100 text-sm mb-1">Total Points</p>
                          <div className="flex items-baseline gap-3">
                            <h2 className="text-4xl font-bold text-white">{stats.totalPoints.toLocaleString()}</h2>
                            <p className="text-sky-100 text-sm">${stats.dollarValue}</p>
                          </div>
                        </div>
                        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                          <Trophy className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/10 rounded-2xl p-3">
                          <p className="text-sky-100 text-xs mb-1">This Month</p>
                          <p className="text-white font-semibold">+{stats.thisMonth}</p>
                        </div>
                        <div className="bg-white/10 rounded-2xl p-3">
                          <p className="text-sky-100 text-xs mb-1">Expiring Soon</p>
                          <p className="text-white font-semibold">{stats.expiringSoon} rewards</p>
                        </div>
                      </div>

                      <button className="w-full bg-white text-sky-600 py-3 rounded-2xl font-semibold mt-4 flex items-center justify-center gap-2">
                        <QrCode className="w-5 h-5" />
                        Scan Reciept
                      </button>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="px-6 mt-6">
                    <div className="grid grid-cols-4 gap-4">
                      <button className="flex flex-col items-center gap-2">
                        <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center">
                          <Store className="w-6 h-6 text-orange-600" />
                        </div>
                        <span className="text-xs text-gray-700">Businesses</span>
                      </button>
                      <button className="flex flex-col items-center gap-2">
                        <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-purple-600" />
                        </div>
                        <span className="text-xs text-gray-700">Events</span>
                      </button>
                      <button className="flex flex-col items-center gap-2">
                        <div className="w-14 h-14 bg-pink-100 rounded-2xl flex items-center justify-center">
                          <Gift className="w-6 h-6 text-pink-600" />
                        </div>
                        <span className="text-xs text-gray-700">Rewards</span>
                      </button>
                      <button className="flex flex-col items-center gap-2">
                        <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center">
                          <MapPin className="w-6 h-6 text-green-600" />
                        </div>
                        <span className="text-xs text-gray-700">Near Me</span>
                      </button>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="flex-1 px-6 mt-6 overflow-y-auto pb-20">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                    <div className="space-y-3">
                      {recentActivity.map(activity => (
                        <div key={activity.id} className="bg-white rounded-2xl p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              activity.type === 'earned' ? 'bg-green-100' : 'bg-orange-100'
                            }`}>
                              {activity.type === 'earned' ? (
                                <TrendingUp className="w-5 h-5 text-green-600" />
                              ) : (
                                <Gift className="w-5 h-5 text-orange-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{activity.business}</p>
                              <p className="text-sm text-gray-600">{activity.time}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold ${
                              activity.type === 'earned' ? 'text-green-600' : 'text-orange-600'
                            }`}>
                              {activity.type === 'earned' ? '+' : '-'}{activity.points} pts
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tab Bar */}
                  <div className={components.tabBar.container}>
                    <div className="flex items-center justify-around py-2">
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Home className="w-6 h-6 text-sky-600" />
                        <span className="text-xs text-sky-600">Home</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Search className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Discover</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Wallet className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Wallet</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <User className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Profile</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Points & Rewards Focus</p>
        </div>

        {/* Solution 2: Discovery-Focused */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-11 h-full bg-white flex flex-col">
                  {/* Header with Points */}
                  <div className="px-6 pt-4 pb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h1 className="text-2xl font-bold text-gray-900">Discover</h1>
                      <div className="flex items-center gap-3">
                        <div className="bg-amber-50 px-3 py-1.5 rounded-full flex items-center gap-2">
                          <Star className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-semibold text-amber-900">{stats.totalPoints}</span>
                        </div>
                        <button className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <Menu className="w-5 h-5 text-gray-700" />
                        </button>
                      </div>
                    </div>

                    {/* Search Bar */}
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input 
                        type="text" 
                        placeholder="Search businesses or events..."
                        className="w-full bg-gray-100 rounded-2xl pl-12 pr-4 py-3 text-gray-900 placeholder-gray-500"
                      />
                    </div>
                  </div>

                  {/* Content Scroll Area */}
                  <div className="flex-1 overflow-y-auto pb-20">
                    {/* Featured Section */}
                    <div className="px-6 mb-6">
                      <div className="bg-gradient-to-r from-pink-500 to-orange-500 rounded-3xl p-6 text-white">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold mb-2">Weekend Special</h3>
                            <p className="text-white/90">Triple points at all restaurants!</p>
                          </div>
                          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                            <Sparkles className="w-6 h-6" />
                          </div>
                        </div>
                        <button className="bg-white text-pink-600 px-6 py-2 rounded-xl font-semibold">
                          View Offers
                        </button>
                      </div>
                    </div>

                    {/* Categories */}
                    <div className="px-6 mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        <button className="flex-shrink-0 bg-sky-100 text-sky-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2">
                          <Store className="w-4 h-4" />
                          All
                        </button>
                        <button className="flex-shrink-0 bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2">
                          <Coffee className="w-4 h-4" />
                          Coffee
                        </button>
                        <button className="flex-shrink-0 bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2">
                          <Pizza className="w-4 h-4" />
                          Dining
                        </button>
                        <button className="flex-shrink-0 bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2">
                          <ShoppingBag className="w-4 h-4" />
                          Shopping
                        </button>
                      </div>
                    </div>

                    {/* Featured Businesses */}
                    <div className="px-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Near You</h3>
                        <button className="text-sky-600 text-sm font-medium">View Map</button>
                      </div>
                      <div className="space-y-4">
                        {featuredBusinesses.map(business => (
                          <div key={business.id} className="bg-gray-50 rounded-2xl overflow-hidden">
                            <div className="h-32 bg-gray-200 relative">
                              <img 
                                src={business.image} 
                                alt={business.name}
                                className="w-full h-full object-cover"
                              />
                              {business.pointsMultiplier && (
                                <div className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                  {business.pointsMultiplier}x Points
                                </div>
                              )}
                              {business.hasPromo && (
                                <div className="absolute top-3 left-3 bg-pink-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                  Promo
                                </div>
                              )}
                            </div>
                            <div className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{business.name}</h4>
                                  <p className="text-sm text-gray-600">{business.category} • {business.distance}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                  <span className="text-sm font-medium text-gray-900">{business.rating}</span>
                                </div>
                              </div>
                              <p className="text-sm text-sky-600 font-medium">{business.offer}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Tab Bar */}
                  <div className={components.tabBar.container}>
                    <div className="flex items-center justify-around py-2">
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Home className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Home</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Search className="w-6 h-6 text-sky-600" />
                        <span className="text-xs text-sky-600">Discover</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Wallet className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Wallet</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <User className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Profile</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Business Discovery</p>
        </div>

        {/* Solution 3: Event-Focused */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-11 h-full bg-gray-50 flex flex-col">
                  {/* Compact Header */}
                  <div className="bg-white px-6 pt-4 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                        <div>
                          <p className="text-xs text-gray-600">Welcome back!</p>
                          <p className="font-semibold text-gray-900">Sarah Chen</p>
                        </div>
                      </div>
                      <button className="bg-sky-500 text-white px-4 py-2 rounded-xl font-medium flex items-center gap-2">
                        <QrCode className="w-4 h-4" />
                        Scan
                      </button>
                    </div>
                  </div>

                  {/* Points Summary Bar */}
                  <div className="bg-gradient-to-r from-sky-500 to-sky-600 px-6 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div>
                          <p className="text-xs text-sky-100">Balance</p>
                          <p className="text-lg font-bold text-white">{stats.totalPoints} pts</p>
                        </div>
                        <div className="w-px h-8 bg-white/30"></div>
                        <div>
                          <p className="text-xs text-sky-100">This Week</p>
                          <p className="text-lg font-bold text-white">+125</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto pb-20">
                    {/* Upcoming Events */}
                    <div className="px-6 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">This Weekend</h3>
                        <button className="text-sky-600 text-sm font-medium">All Events</button>
                      </div>
                      <div className="space-y-4">
                        {upcomingEvents.map(event => (
                          <div key={event.id} className="bg-white rounded-2xl p-4 shadow-sm">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 mb-1">{event.name}</h4>
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>{event.date}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    <span>{event.time}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 mt-2">
                                  <MapPin className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-600">{event.location}</span>
                                </div>
                              </div>
                              <div className="bg-amber-100 px-3 py-1 rounded-full">
                                <span className="text-xs font-semibold text-amber-700">+{event.bonusPoints} pts</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="flex -space-x-2">
                                  {[...Array(3)].map((_, i) => (
                                    <div key={i} className="w-6 h-6 bg-gray-200 rounded-full border-2 border-white"></div>
                                  ))}
                                </div>
                                <span className="text-sm text-gray-600">{event.attendees} going</span>
                              </div>
                              <button className="text-sky-600 font-medium text-sm">RSVP</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Hot Deals */}
                    <div className="px-6 mt-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Hot Deals</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white rounded-2xl p-4 shadow-sm">
                          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center mb-3">
                            <Percent className="w-5 h-5 text-orange-600" />
                          </div>
                          <h4 className="font-medium text-gray-900 text-sm mb-1">Happy Hour</h4>
                          <p className="text-xs text-gray-600 mb-2">3-6 PM Daily</p>
                          <p className="text-xs font-semibold text-orange-600">5 places</p>
                        </div>
                        <div className="bg-white rounded-2xl p-4 shadow-sm">
                          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
                            <Tag className="w-5 h-5 text-purple-600" />
                          </div>
                          <h4 className="font-medium text-gray-900 text-sm mb-1">Flash Sales</h4>
                          <p className="text-xs text-gray-600 mb-2">Limited Time</p>
                          <p className="text-xs font-semibold text-purple-600">12 active</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tab Bar */}
                  <div className={components.tabBar.container}>
                    <div className="flex items-center justify-around py-2">
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Home className="w-6 h-6 text-sky-600" />
                        <span className="text-xs text-sky-600">Home</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Search className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Discover</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Wallet className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Wallet</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <User className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Profile</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Events & Activities</p>
        </div>

      </div>
    </div>
  );
}

export default Dashboard;